import { Box, Typography, Chip } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { makeStyles } from "@mui/styles";

import synchroYou from "../../assets/broadcast-moments/kbs-synchro-you-2024.jpg";
import namjin from "../../assets/broadcast-moments/namjin-ai-concert-2024.jpg";
import yimJaeBum from "../../assets/broadcast-moments/yim-jaebeom-ai-hidden-singer.jpg";
import deux from "../../assets/broadcast-moments/deux-ai-2025.jpg";

const FEATURED = {
  image: deux,
  badges: ["LATEST", "MUSIC PRODUCTION"],
  meta: "DEUX · 2025",
  title: (
    <>
      듀스, AI로 다시
      <br />
      무대에 서다.
    </>
  ),
  description:
    "전설의 듀스(DEUX) — 그 시절의 사운드와 보이스를 AI로 정교하게 되살려, SoriSori AI가 함께한 신곡 작업.",
  cta: "WATCH NOW",
  href: "https://www.youtube.com/watch?v=_N4nrc2L-GQ",
};

const PREVIEW = [
  {
    image: synchroYou,
    badge: "KBS 싱크로유",
    meta: "2024",
    title: "최정상 가수들과 펼치는 뮤직 버라이어티",
    href: "https://www.youtube.com/watch?v=9hWEtE56KSg",
  },
  {
    image: namjin,
    badge: "NAMJIN",
    meta: "2024",
    title: "청춘 남진과 현재 남진의 컬래버 무대",
    href: "https://www.youtube.com/watch?v=QU2A0jeEqzY",
  },
  {
    image: yimJaeBum,
    badge: "YIM JAE BUM",
    meta: "2024",
    title: "임재범 vs AI 임재범 — 진짜를 찾아라",
    href: "https://www.youtube.com/watch?v=vZeEzZe1B-4",
  },
];

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(14, 4, 16),
    color: theme.palette.text.primary,
    "@media (max-width:600px)": { padding: theme.spacing(8, 3, 10) },
  },
  inner: {
    maxWidth: 1280,
    margin: "0 auto",
  },
  topTag: {
    fontSize: 12,
    letterSpacing: "0.28em",
    color: "rgba(255,255,255,0.5)",
    marginBottom: theme.spacing(3),
  },
  topTagAccent: {
    color: theme.palette.primary.main,
    fontWeight: 600,
  },
  headRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(6),
    alignItems: "flex-end",
    justifyContent: "space-between",
    marginBottom: theme.spacing(7),
    "@media (max-width:600px)": {
      gap: theme.spacing(2),
      marginBottom: theme.spacing(5),
    },
  },
  headline: {
    fontSize: "clamp(36px, 5vw, 60px)",
    lineHeight: 1.05,
    fontWeight: 800,
    letterSpacing: "-0.02em",
    maxWidth: 600,
  },
  headDesc: {
    color: "rgba(255,255,255,0.65)",
    fontSize: 15,
    lineHeight: 1.6,
    maxWidth: 360,
  },
  /* featured */
  featured: {
    display: "grid",
    gridTemplateColumns: "1.1fr 1fr",
    gap: theme.spacing(0),
    backgroundColor: theme.palette.background.paper,
    borderRadius: 16,
    overflow: "hidden",
    border: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(5),
    textDecoration: "none",
    color: "inherit",
    cursor: "pointer",
    transition: "border-color 0.3s",
    "&:hover": { borderColor: "rgba(255,255,255,0.18)" },
    "@media (max-width:900px)": { gridTemplateColumns: "1fr" },
  },
  featuredImage: {
    width: "100%",
    height: "100%",
    minHeight: 360,
    objectFit: "cover",
    display: "block",
    "@media (max-width:600px)": { minHeight: 220 },
  },
  featuredBody: {
    padding: theme.spacing(5, 5, 4),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    "@media (max-width:600px)": { padding: theme.spacing(3, 3, 3) },
  },
  featuredImageWrap: {
    "@media (max-width:900px)": { aspectRatio: "16 / 10" },
  },
  badgeRow: { display: "flex", gap: theme.spacing(1), flexWrap: "wrap" },
  badgeFeatured: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    fontWeight: 700,
    height: 24,
    fontSize: 11,
    letterSpacing: "0.1em",
    borderRadius: 999,
  },
  badgeMuted: {
    backgroundColor: "rgba(255,255,255,0.08)",
    color: theme.palette.text.primary,
    fontWeight: 600,
    height: 24,
    fontSize: 11,
    letterSpacing: "0.1em",
    borderRadius: 999,
  },
  meta: {
    fontSize: 12,
    letterSpacing: "0.16em",
    color: "rgba(255,255,255,0.55)",
    marginTop: theme.spacing(0.5),
  },
  featuredTitle: {
    fontSize: 28,
    fontWeight: 800,
    lineHeight: 1.25,
    color: theme.palette.primary.main,
    letterSpacing: "-0.01em",
    "@media (max-width:600px)": { fontSize: 22 },
  },
  featuredDesc: {
    fontSize: 14,
    lineHeight: 1.7,
    color: "rgba(255,255,255,0.75)",
  },
  watchBtn: {
    alignSelf: "flex-start",
    color: theme.palette.primary.main,
    border: `1px solid ${theme.palette.primary.main}`,
    borderRadius: 999,
    padding: theme.spacing(0.75, 2.25),
    fontSize: 12,
    letterSpacing: "0.16em",
    fontWeight: 700,
    marginTop: theme.spacing(1),
    "&:hover": {
      backgroundColor: "rgba(223,255,60,0.08)",
    },
  },
  /* preview row (compact) */
  previewGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: theme.spacing(3),
    marginTop: theme.spacing(5),
    "@media (max-width:900px)": {
      gridTemplateColumns: "1fr",
      gap: theme.spacing(2.5),
    },
  },
  previewCard: {
    background: theme.palette.background.paper,
    borderRadius: 14,
    overflow: "hidden",
    border: `1px solid ${theme.palette.divider}`,
    display: "flex",
    flexDirection: "column",
    textDecoration: "none",
    color: "inherit",
    cursor: "pointer",
    transition: "transform .3s, border-color .3s",
    "&:hover": {
      transform: "translateY(-4px)",
      borderColor: "rgba(255,255,255,0.18)",
    },
    "&:hover $previewImg": { transform: "scale(1.05)" },
  },
  previewImgWrap: {
    width: "100%",
    aspectRatio: "16 / 10",
    overflow: "hidden",
    position: "relative",
  },
  previewImg: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
    transition: "transform .55s cubic-bezier(.2,.7,.2,1)",
  },
  previewBody: {
    padding: theme.spacing(2.25, 2.25, 2.5),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.75),
  },
  previewMeta: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: 11,
    letterSpacing: "0.18em",
    color: "rgba(255,255,255,0.5)",
    fontWeight: 600,
  },
  previewTitle: {
    fontSize: 15,
    fontWeight: 700,
    lineHeight: 1.4,
    marginTop: theme.spacing(0.5),
    letterSpacing: "-0.2px",
  },
  /* view all CTA */
  viewAllRow: {
    display: "flex",
    justifyContent: "center",
    marginTop: theme.spacing(6),
  },
  viewAllBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "12px 26px",
    borderRadius: 999,
    border: "1px solid rgba(255,255,255,0.14)",
    background: "rgba(255,255,255,0.02)",
    color: "rgba(255,255,255,0.85)",
    fontSize: 13,
    fontWeight: 600,
    letterSpacing: "1px",
    textDecoration: "none",
    transition: "background .2s, border-color .2s, color .2s, gap .25s",
    "&:hover": {
      background: "rgba(255,255,255,0.06)",
      borderColor: "rgba(255,255,255,0.3)",
      color: "#fff",
      gap: 12,
    },
  },
}));

export default function BroadcastMoments() {
  const classes = useStyles();
  return (
    <Box component="section" className={classes.root}>
      <Box className={classes.inner}>
        <Typography className={classes.topTag}>
          <span className={classes.topTagAccent}>MEDIA</span> &nbsp;—&nbsp;
          PRESS &amp; PRODUCTIONS
        </Typography>

        <Box className={classes.headRow}>
          <Typography variant="h2" className={classes.headline}>
            Media.
          </Typography>
          <Typography className={classes.headDesc}>
            방송, 라디오, 영상 — SoriSori AI의 기술이 닿은 순간들.
          </Typography>
        </Box>

        {/* featured card */}
        <Box
          className={classes.featured}
          component="a"
          href={FEATURED.href}
          target="_blank"
          rel="noopener noreferrer"
        >
          <img
            className={classes.featuredImage}
            src={FEATURED.image}
            alt="듀스 AI 신곡 프로덕션"
          />
          <Box className={classes.featuredBody}>
            <Box className={classes.badgeRow}>
              <Chip
                label={FEATURED.badges[0]}
                className={classes.badgeFeatured}
              />
              <Chip label={FEATURED.badges[1]} className={classes.badgeMuted} />
            </Box>
            <Typography className={classes.meta}>{FEATURED.meta}</Typography>
            <Typography className={classes.featuredTitle}>
              {FEATURED.title}
            </Typography>
            <Typography className={classes.featuredDesc}>
              {FEATURED.description}
            </Typography>
            <Box className={classes.watchBtn}>{FEATURED.cta} →</Box>
          </Box>
        </Box>

        {/* compact preview row (3 curated highlights) */}
        <Box className={classes.previewGrid}>
          {PREVIEW.map((item) => (
            <Box
              key={item.title}
              component="a"
              href={item.href}
              target="_blank"
              rel="noopener noreferrer"
              className={classes.previewCard}
            >
              <Box className={classes.previewImgWrap}>
                <img
                  src={item.image}
                  alt={item.title}
                  className={classes.previewImg}
                />
              </Box>
              <Box className={classes.previewBody}>
                <Box className={classes.previewMeta}>
                  <span>{item.badge}</span>
                  <span>{item.meta}</span>
                </Box>
                <Typography className={classes.previewTitle}>
                  {item.title}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>

        <Box className={classes.viewAllRow}>
          <Box
            component={RouterLink}
            to="/media"
            className={classes.viewAllBtn}
          >
            VIEW ALL PRODUCTIONS →
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
